import { useEffect, useState } from "react";
import { Loader2, Play, AlertTriangle, CheckCircle2, Download, Info, CloudUpload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

interface BackupRunInfo {
  at: string;
  ok: boolean;
  key?: string;
  size?: number;
  durationMs: number;
  error?: string;
}

interface BackupObject {
  key: string;
  size: number;
  lastModified: string | null;
}

interface BackupStatusResponse {
  configured: boolean;
  running: boolean;
  lastRun: BackupRunInfo | null;
  backups: BackupObject[];
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("bs-BA", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function backupName(key: string): string {
  return key.replace(/^backups\//, "");
}

export function BackupSection() {
  const [data, setData] = useState<BackupStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  async function load() {
    try {
      const res = await fetch("/api/backup");
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Greška pri učitavanju");
      setData(json as BackupStatusResponse);
      setError(null);
    } catch (e: any) {
      setError(e?.message || "Greška pri učitavanju");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleRun() {
    setRunning(true);
    try {
      const res = await fetch("/api/backup/run", { method: "POST" });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Greška pri backupu");
      toast.success("Backup uspješno završen");
    } catch (e: any) {
      toast.error(e?.message || "Greška pri backupu");
    } finally {
      setRunning(false);
      await load();
    }
  }

  async function handleDownload(key: string) {
    setDownloading(key);
    try {
      const res = await fetch("/api/backup/download-url", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Greška pri preuzimanju");
      window.open(json.url, "_blank");
    } catch (e: any) {
      toast.error(e?.message || "Greška pri preuzimanju");
    } finally {
      setDownloading(null);
    }
  }

  async function handleDelete(key: string) {
    if (!confirm(`Obrisati backup "${backupName(key)}"?`)) return;
    setDeleting(key);
    try {
      const res = await fetch("/api/backup/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Greška pri brisanju");
      toast.success("Backup obrisan");
    } catch (e: any) {
      toast.error(e?.message || "Greška pri brisanju");
    } finally {
      setDeleting(null);
      await load();
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground p-4">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Učitavanje…
      </div>
    );
  }

  if (data && !data.configured) {
    return (
      <div className="ring-1 ring-foreground/10">
        <div className="border-b bg-muted/50 px-4 py-2">
          <span className="text-xs font-medium">R2 Backup</span>
        </div>
        <div className="p-4">
          <div className="flex items-start gap-2 bg-blue-500/10 text-blue-700 dark:text-blue-400 px-3 py-2 text-xs max-w-xl">
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>
              R2 backup nije konfigurisan. Postavi <code>R2_ACCOUNT_ID</code>,{" "}
              <code>R2_ACCESS_KEY_ID</code>, <code>R2_SECRET_ACCESS_KEY</code> i{" "}
              <code>R2_BUCKET</code> env varijable pa restartuj aplikaciju.
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="ring-1 ring-foreground/10">
        <div className="border-b bg-muted/50 px-4 py-2">
          <span className="text-xs font-medium">R2 Backup baze</span>
        </div>
        <div className="p-4 flex flex-col gap-3">
          <p className="text-xs text-muted-foreground max-w-xl">
            Baza se automatski backupuje na Cloudflare R2 svakih 12 sati. Backupi
            stariji od 7 dana se automatski brišu.
          </p>

          {error && (
            <div className="flex items-start gap-2 bg-red-500/10 text-red-700 dark:text-red-400 px-3 py-2 text-xs max-w-xl">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {data?.lastRun && (
            <div className="flex items-center gap-2 text-xs">
              {data.lastRun.ok ? (
                <>
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                  <span>
                    Zadnji backup: {formatDate(data.lastRun.at)}
                    {data.lastRun.size != null && (
                      <span className="text-muted-foreground"> · {formatBytes(data.lastRun.size)}</span>
                    )}
                  </span>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-3.5 w-3.5 text-red-600" />
                  <span>
                    Zadnji backup nije uspio ({formatDate(data.lastRun.at)}):{" "}
                    <span className="text-red-700 dark:text-red-400">{data.lastRun.error}</span>
                  </span>
                </>
              )}
            </div>
          )}
          {!data?.lastRun && (
            <span className="text-xs text-muted-foreground">
              Nijedan backup nije pokrenut od zadnjeg restarta aplikacije.
            </span>
          )}

          <div className="mt-1">
            <Button onClick={handleRun} disabled={running || data?.running}>
              {running || data?.running ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Play className="mr-2 h-4 w-4" />
              )}
              Pokreni backup sada
            </Button>
          </div>
        </div>
      </div>

      <div className="ring-1 ring-foreground/10">
        <div className="border-b bg-muted/50 px-4 py-2 flex items-center gap-2">
          <CloudUpload className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-xs font-medium">Backupi na R2 ({data?.backups.length ?? 0})</span>
        </div>
        {data && data.backups.length === 0 ? (
          <p className="p-4 text-xs text-muted-foreground">Nema backupa na R2.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-muted/30 text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-1.5 font-medium">Naziv</th>
                <th className="text-left px-3 py-1.5 font-medium">Datum</th>
                <th className="text-right px-3 py-1.5 font-medium">Veličina</th>
                <th className="px-3 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {data?.backups.map((b) => (
                <tr key={b.key} className="border-t">
                  <td className="px-3 py-1.5 font-mono">{backupName(b.key)}</td>
                  <td className="px-3 py-1.5 tabular-nums">{formatDate(b.lastModified)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{formatBytes(b.size)}</td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(b.key)}
                        disabled={downloading !== null || deleting !== null}
                      >
                        {downloading === b.key ? (
                          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Download className="mr-1.5 h-3.5 w-3.5" />
                        )}
                        Preuzmi
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 dark:text-red-400"
                        onClick={() => handleDelete(b.key)}
                        disabled={deleting !== null || downloading !== null}
                      >
                        {deleting === b.key ? (
                          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                        )}
                        Obriši
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
